import Grid from '@mui/material/Grid';
import { Button } from '@openx/components-core/lib/Button/Button';
import type { ContextualSegmentsDrawerState } from '@openx/types/targeting/advancedTargeting';
import type { CustomVariablesDataPayload } from '@openx/types/targeting/customVariables';
import { noop } from '@openx/utils/noop';
import type { Dispatch, JSX, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkAdd } from '../shared/BulkAdd/useBulkAdd';
import { ContextualSegmentsBulkV2 } from './ContextualSegmentsV2/ContextualSegmentsBulkV2';
import { useGetContextualSegmentsComponents } from './hooks/useGetContextualSegmentsComponents';

type ContextualSegmentsDrawerTopProps = {
  params: ContextualSegmentsDrawerState;
  updateParams: Dispatch<SetStateAction<ContextualSegmentsDrawerState>>;
  onAdd: (contextual: CustomVariablesDataPayload[]) => void;
  accountUid: string;
  besideTypeSelector?: boolean;
};

/** Contextual segments (V1/V2) drawer inputs: provider/segment top box and, for V2, Bulk Add. */
export const ContextualSegmentsDrawerTop = ({
  params,
  updateParams,
  onAdd,
  accountUid,
  besideTypeSelector = false,
}: ContextualSegmentsDrawerTopProps): JSX.Element => {
  const { t } = useTranslation();
  const { ContextualSegmentsTopBox, segmentsV2 } = useGetContextualSegmentsComponents();
  const { isBulkOpen, onBulkClose, onBulkOpen } = useBulkAdd({ onAdd: noop });

  return (
    <>
      <ContextualSegmentsTopBox
        items={params}
        onEnter={onAdd}
        sx={{ borderBottom: 0, paddingBottom: 0, paddingTop: 0 }}
        updateParams={updateParams}
        accountUid={accountUid}
        besideTypeSelector={besideTypeSelector}
      />

      {segmentsV2 && (
        <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end', paddingRight: theme => theme.spacing(3) }}>
          <Button onClick={onBulkOpen} variant="text" data-test="bulk-add-button" disabled={!params.contextualV2.attr}>
            {t('Bulk Add')}
          </Button>
        </Grid>
      )}

      <ContextualSegmentsBulkV2
        isOpen={isBulkOpen}
        closeDrawer={onBulkClose}
        contextualItems={params.contextualV2}
        updateParams={updateParams}
      />
    </>
  );
};
